import React, { forwardRef, useEffect, useState } from "react";

export const PRODUCTION_PAGE_SIZE = 25;
export const PRODUCTION_REVEAL_SIZE = 5;

// Owns display progress only. Scroll never performs an API request. The parent
// retains the complete cached page for scheduling, details and cross-user sync.
const ProductionLaneList = forwardRef(function ProductionLaneList({
  cards = [], page = 1, hasMore = false, loading = false, error = null,
  busyLabel = "", onLoadMore, children, navigation, replacePage = false, ...listProps
}, ref) {
  const [progress, setProgress] = useState({ page, shown: PRODUCTION_REVEAL_SIZE, boundary: PRODUCTION_PAGE_SIZE });
  let current = progress;
  if (page !== progress.page) {
    current = {
      page,
      shown: !replacePage && page > progress.page ? progress.shown + PRODUCTION_REVEAL_SIZE : PRODUCTION_REVEAL_SIZE,
      boundary: !replacePage && page > progress.page ? progress.boundary + PRODUCTION_PAGE_SIZE : PRODUCTION_PAGE_SIZE,
    };
    setProgress(current);
  }
  const boundary = current.boundary;
  const shown = Math.min(current.shown, cards.length, boundary);
  const cachedEnd = Math.min(cards.length, boundary);
  const atBoundary = shown >= cachedEnd;
  const busy = loading || !!busyLabel;

  // One automatic reveal per display block. Scroll arriving before this frame
  // cannot reveal the same five twice; catch up to a target rather than add 5.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const target = Math.min(boundary - PRODUCTION_PAGE_SIZE + 2 * PRODUCTION_REVEAL_SIZE, cards.length);
      setProgress((previous) => previous.boundary !== boundary || previous.shown >= target
        ? previous : { ...previous, shown: target });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [boundary, cards.length]);

  function revealOnScroll(event) {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight >= 100 || shown >= cachedEnd) return;
    setProgress((previous) => ({
      ...previous,
      shown: Math.min(previous.shown + PRODUCTION_REVEAL_SIZE, cachedEnd),
    }));
  }

  function loadNextBlock() {
    if (busy) return;
    if (cards.length > boundary) {
      setProgress((previous) => previous.boundary !== boundary ? previous : {
        ...previous,
        boundary: previous.boundary + PRODUCTION_PAGE_SIZE,
        shown: Math.min(previous.shown + PRODUCTION_REVEAL_SIZE, cards.length),
      });
    } else {
      onLoadMore?.();
    }
  }

  return (
    <div {...listProps} ref={ref} onScroll={revealOnScroll}>
      {children(cards.slice(0, shown))}
      {cards.length > 0 && (
        <div style={{ padding: 10, textAlign: "center", fontSize: 12 }}>
          <div>{`Showing ${shown} of ${cards.length} loaded cards`}</div>
          {shown < cachedEnd && <div>Scroll to show 5 more cards</div>}
        </div>
      )}
      {loading && <div role="status" style={{ padding: 12, textAlign: "center" }}>
        <span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading more cards...
      </div>}
      {error && <div role="alert" style={{ padding: 10, color: "#b91c1c" }}>{error}</div>}
      {atBoundary && (cards.length > boundary || hasMore) && (
        <div style={{ padding: 12, textAlign: "center" }}>
          <button type="button" onClick={loadNextBlock} disabled={busy}>
            {loading ? "Loading more cards..." : busyLabel || (error ? "Retry" : "Load More Cards")}
          </button>
        </div>
      )}
      {navigation?.({ atBoundary, busy })}
    </div>
  );
});

export default ProductionLaneList;
